import { CommonModule } from '@angular/common'
import { Component, DestroyRef, EventEmitter, Output, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'

import { TooltipModule } from 'primeng/tooltip'

import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { FlxDataModelAutocompleteComponent } from '@flowx/designer/shared/components/data-model-autocomplete/data-model-autocomplete.component'
import { DataModelAutocompleteService } from '@flowx/designer/shared/services/data-model-autocomplete.service'
import {
  FLX_MODAL_DATA,
  FlxFormControlModule,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { FlxIconModule } from '@flowx/angular-ui-toolkit'

import { DataModelDraftItem } from '../../../../common/ai.dictionary'

enum DataModelItemStatus {
  CREATED = 'created',
  MAPPED = 'mapped',
}

const statusTooltipMap = {
  [DataModelItemStatus.CREATED]: 'A new data key will be created for this component',
  [DataModelItemStatus.MAPPED]: 'This component will be mapped to an existing data key',
}

type DataModelItem = {
  origDataModelItem: DataModelDraftItem
  label: string
  type: DataModelAttributeType
  status: DataModelItemStatus
  form: FormGroup<{ active: FormControl; key: FormControl; description: FormControl }>
}

@Component({
  selector: 'flx-ai-data-model-modify-modal',
  imports: [
    CommonModule,
    TooltipModule,
    FlxIconModule,
    FlxModalModule,
    FlxFormControlModule,
    FormsModule,
    ReactiveFormsModule,
    FlxDataModelAutocompleteComponent,
  ],
  templateUrl: './ai-data-model-modify-modal.component.html',
  styleUrls: ['./ai-data-model-modify-modal.component.scss'],
})
export class AiDataModelModifyModalComponent {
  #destroyRef = inject(DestroyRef)
  #data = inject(FLX_MODAL_DATA)
  #modalService = inject(FlxModalService)
  #dataModelAutocompleteService = inject(DataModelAutocompleteService)

  @Output() dataEventEmitter = new EventEmitter<any>()

  DataModelItemStatus = DataModelItemStatus
  statusTooltipMap = statusTooltipMap

  dataModelItems: DataModelItem[] = []

  initialFormSerialized: string = null

  constructor() {
    this.dataModelItems = [
      ...this.#data.dataModel.new.map((newDataModel: DataModelDraftItem) =>
        this.#draftDMtoDataModelItem(newDataModel, DataModelItemStatus.CREATED)
      ),
      ...this.#data.dataModel.existing.map((existingDataModel: DataModelDraftItem) =>
        this.#draftDMtoDataModelItem(existingDataModel, DataModelItemStatus.MAPPED)
      ),
    ]

    this.dataModelItems.forEach((dataModelItem, index) =>
      this.#setupFormWatchers(index, dataModelItem.form)
    )

    this.initialFormSerialized = this.#dataModelItemsFormsSerialize(this.dataModelItems)
  }

  canSaveChanges(): boolean {
    const hasErrors = this.dataModelItems.some((dataModelItem) => !dataModelItem.form.valid)
    const hasActiveItems = this.dataModelItems.some(
      (dataModelItem) => dataModelItem.form.controls.active.value
    )
    const hasChanges =
      this.initialFormSerialized !== this.#dataModelItemsFormsSerialize(this.dataModelItems)

    return !hasErrors && hasActiveItems && hasChanges
  }

  saveChanges(): void {
    const updatedDataModel = this.dataModelItems.reduce(
      (acc, dataModelItem) => {
        const draftDataModel = this.#dataModelItemToDraftDM(dataModelItem)

        if (dataModelItem.status === DataModelItemStatus.CREATED) {
          acc.new.push(draftDataModel)
        } else {
          acc.existing.push(draftDataModel)
        }

        return acc
      },
      {
        new: [],
        existing: [],
      }
    )

    this.dataEventEmitter.emit({ confirm: true, updatedDataModel })
    this.closeModal()
  }

  closeModal(): void {
    this.#modalService.hide()
  }

  #dataModelItemsFormsSerialize(dataModelItems: DataModelItem[]): string {
    const formsData: Record<string, unknown> = dataModelItems.reduce(
      (acc, dataModelItem) => ({
        ...acc,
        [dataModelItem.form.value.key]: dataModelItem.form.value,
      }),
      {}
    )

    return JSON.stringify(formsData)
  }

  #setupFormWatchers(index: number, dataModelForm: FormGroup): void {
    const dataModelItem = this.dataModelItems[index]

    dataModelForm.controls.active.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((active) => {
        if (!active) {
          dataModelForm.controls.key.disable()
          dataModelForm.controls.description.disable()
        } else {
          dataModelForm.controls.key.enable()
          dataModelForm.controls.description.enable()
        }
      })

    dataModelForm.controls.key.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((nextKey) => {
        const dataModelSearchResults = this.#dataModelAutocompleteService.filterDataModel(nextKey)
        const dataModelSearchMatch = dataModelSearchResults.find((item) => nextKey === item.path)

        dataModelItem.type = dataModelSearchMatch?.type ?? dataModelItem.origDataModelItem.type
        dataModelForm.patchValue({
          description:
            dataModelSearchMatch?.description ??
            dataModelItem.origDataModelItem.description ??
            null,
        })
        dataModelItem.status = dataModelSearchMatch
          ? DataModelItemStatus.MAPPED
          : DataModelItemStatus.CREATED
      })
  }

  #draftDMtoDataModelItem(
    dataModel: DataModelDraftItem,
    status: DataModelItemStatus
  ): DataModelItem {
    const { key, label, type, description, inactive } = dataModel

    const form = new FormGroup({
      active: new FormControl(!inactive),
      key: new FormControl(key, [Validators.required]),
      description: new FormControl(description),
    })

    return { origDataModelItem: dataModel, label, type, status, form }
  }

  #dataModelItemToDraftDM(dataModelItem: DataModelItem): Omit<DataModelDraftItem, 'properties'> {
    const { origDataModelItem, label, type, form } = dataModelItem
    const formValue = form.getRawValue()

    return {
      ...origDataModelItem,
      label,
      type,
      key: formValue.key,
      description: formValue.description,
      inactive: !formValue.active,
    }
  }
}
